<script setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineProps({
  documents: {
    type: Array,
    default: () => [],
  },
  state: {
    type: String,
    default: "done",
  },
  skeletonCount: {
    type: Number,
    default: 3,
  },
});

const emit = defineEmits(["select"]);

const openDocId = ref(null);

function pluralRu(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ${t(one)}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
    return `${n} ${t(few)}`;
  return `${n} ${t(many)}`;
}

function pluralErrors(n) {
  return pluralRu(n, "замечание", "замечания", "замечаний");
}

function pluralWarnings(n) {
  return pluralRu(n, "предупреждение", "предупреждения", "предупреждений");
}

const ROW_CLASS =
  "w-full text-left flex items-center gap-3 px-3 sm:px-4 py-3 sm:py-3 transition-colors hover:bg-muted/30 cursor-pointer bg-card";

function fieldStatusColor(ok) {
  // Подсвечиваем красным только явно проблемные поля; всё остальное (true/null/undefined) — нейтральный цвет
  if (ok === false || ok === "bad") return "ai-field--bad";
  if (ok === "warn") return "ai-field--warn";
  return "ai-field--ok";
}

function toggle(id) {
  openDocId.value = openDocId.value === id ? null : id;
  emit("select", id);
}
</script>

<template>
  <section class="ai-doc-list flex flex-col h-full rounded overflow-hidden">
    <div class="ai-doc-list__header flex items-center gap-2.5">
      <svg-icon
        name="huge-ai"
        class="ai-doc-list__icon w-4 h-4 shrink-0 fill-none mt-0.5"
      />
      <div class="flex-1 min-w-0">
        <div class="ai-doc-list__title">
          {{ $t("Анализ документов") }}
        </div>
        <p class="ai-doc-list__subtitle truncate">
          {{ "кликните по документу, чтобы увидеть заключение AI" }}
        </p>
      </div>
    </div>

    <div class="flex flex-col gap-2.5 p-3 sm:p-3.5 h-full ai-doc-list__body">
      <!-- Skeleton-карточки пока state=running и реальных документов ещё нет -->
      <template v-if="state === 'running' && (!documents || !documents.length)">
        <div
          v-for="i in skeletonCount"
          :key="`skeleton-${i}`"
          class="ai-doc-card ai-doc-card--skeleton rounded overflow-hidden"
        >
          <div class="flex items-center gap-3 px-3 sm:px-4 py-3 bg-card">
            <div
              class="ai-doc-card__icon ai-doc-card__icon--skeleton shrink-0"
            ></div>
            <div class="flex-1 min-w-0 flex flex-col gap-1.5">
              <div class="ai-doc-skeleton-bar ai-doc-skeleton-bar--title"></div>
              <div class="ai-doc-skeleton-bar ai-doc-skeleton-bar--meta"></div>
            </div>
          </div>
        </div>
      </template>

      <div
        v-for="(doc, i) in documents"
        :key="doc.id"
        :class="[
          'ai-doc-card rounded overflow-hidden ',
          openDocId === doc.id && doc.status === 'bad'
            ? 'ai-doc-card--bad'
            : openDocId === doc.id && doc.status === 'warn'
            ? 'ai-doc-card--warn'
            : '',
          state !== 'done'
            ? 'animate-pulse pointer-events-none'
            : openDocId !== null && openDocId !== doc.id
            ? ''
            : '',
          state === 'done' ? '' : '',
        ]"
        :style="state === 'done' ? { '--reveal-delay': i * 70 + 'ms' } : {}"
      >
        <!-- Row -->
        <div
          role="button"
          tabindex="0"
          :class="ROW_CLASS"
          @click="toggle(doc.id)"
          @keydown.enter.prevent="toggle(doc.id)"
          @keydown.space.prevent="toggle(doc.id)"
        >
          <div
            :class="[
              'ai-doc-card__icon flex items-center justify-center shrink-0',
              state === 'done' && doc.status === 'bad'
                ? 'ai-doc-card__icon--bad'
                : state === 'done' && doc.status === 'warn'
                ? 'ai-doc-card__icon--warn'
                : 'ai-doc-card__icon--ok',
            ]"
          >
            <svg-icon
              :name="
                state === 'done' &&
                (doc.status === 'bad' || doc.status === 'warn')
                  ? 'sli-danger'
                  : 'sli-check-circle'
              "
              class="w-3.5 h-3.5 fill-none"
            />
          </div>

          <div class="flex-1 min-w-0">
            <div class="ai-doc-card__name truncate">
              {{ doc.name }}
            </div>
            <div class="ai-doc-card__meta flex items-center gap-2 flex-wrap">
              <div>{{ doc.size }}</div>
              <div
                v-if="state === 'done' && doc.errorsCount > 0"
                class="ai-doc-card__meta-status ai-doc-card__meta-status--bad"
              >
                {{ pluralErrors(doc.errorsCount) }}
              </div>
              <div
                v-if="state === 'done' && doc.warningsCount > 0"
                class="ai-doc-card__meta-status ai-doc-card__meta-status--warn"
              >
                {{ pluralWarnings(doc.warningsCount) }}
              </div>
            </div>
          </div>

          <svg-icon
            name="huge-shevron-down"
            :class="[
              'ai-doc-card__chevron shrink-0 transition-transform',
              openDocId === doc.id && 'rotate-180',
            ]"
          />
        </div>

        <!-- Expanded panel -->
        <div
          v-if="openDocId === doc.id && state === 'done'"
          class="ai-doc-card__panel"
        >
          <!-- Empty state — нет ни вердикта, ни рекомендаций, ни реквизитов -->
          <div
            v-if="
              !doc.aiVerdict &&
              !doc.aiRecommendation &&
              (!doc.extractedSections || !doc.extractedSections.length)
            "
            class="ai-doc-card__empty flex flex-col items-center justify-center gap-2 p-6 text-center"
          >
            <svg-icon
              name="huge-ai"
              class="w-5 h-5 fill-none text-muted-foreground"
            />
            <p class="ai-doc-card__empty-text">
              {{ $t("AI не вернул деталей по этому документу.") }}
            </p>
            <p class="ai-doc-card__empty-hint">
              {{
                $t(
                  "Возможно, файл не распознан или формат не поддерживается. Попробуйте перепроверить."
                )
              }}
            </p>
          </div>

          <div
            v-else
            class="grid grid-cols-1 md:grid-cols-2 gap-0 ai-doc-card__panel-grid"
          >
            <!-- Left: AI verdict + recommendation (1/2) -->
            <div
              v-if="doc.aiVerdict || doc.aiRecommendation"
              class="ai-doc-card__panel-left flex flex-col gap-3 p-3 sm:p-4"
            >
              <div v-if="doc.aiVerdict" class="flex flex-col gap-1.5">
                <div
                  class="ai-doc-card__section-title ai-doc-card__section-title--ai"
                >
                  {{ $t("Что обнаружил AI") }}
                </div>
                <p class="ai-doc-card__section-text m-0">
                  {{ doc.aiVerdict }}
                </p>
              </div>
              <div v-if="doc.aiRecommendation" class="flex flex-col gap-1.5">
                <div
                  class="ai-doc-card__section-title ai-doc-card__section-title--rec"
                >
                  {{ $t("Рекомендация") }}
                </div>
                <p class="ai-doc-card__section-text m-0">
                  {{ doc.aiRecommendation }}
                </p>
              </div>
            </div>

            <!-- Right: extracted fields, grouped by section (1/2) -->
            <div
              v-if="doc.extractedSections && doc.extractedSections.length"
              class="ai-doc-card__panel-right flex flex-col gap-3 p-3 sm:p-4"
            >
              <div
                class="ai-doc-card__section-title ai-doc-card__section-title--muted"
              >
                {{ $t("Извлечённые реквизиты") }}
              </div>
              <div
                v-for="(section, sIdx) in doc.extractedSections"
                :key="sIdx"
                class="flex flex-col gap-1"
              >
                <div v-if="section.title" class="ai-doc-card__group-title">
                  {{ section.title }}
                </div>
                <div class="flex flex-col">
                  <div
                    v-for="(field, idx) in section.fields"
                    :key="idx"
                    class="extracted-row flex items-start justify-between gap-3 py-2"
                  >
                    <div class="ai-doc-card__field-key shrink-0">
                      {{ field.k }}
                    </div>
                    <div
                      :class="[
                        'ai-doc-card__field-val text-right min-w-0 truncate',
                        fieldStatusColor(field.ok),
                      ]"
                    >
                      {{ field.v }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.ai-doc-list {
  @apply border border-solid border-border bg-muted/30;
  //background: linear-gradient(
  //  to right,
  //  theme("colors.blue.50") 0%,
  //  theme("colors.purple.50") 50%,
  //  theme("colors.white") 100%
  //);

  &__header {
    @apply border-0 border-b border-solid border-border bg-card;
    padding: 14px 14px;
    background: transparent;
  }

  &__title {
    @apply font-semibold;
    font-size: 13px;
    letter-spacing: -0.005em;
    color: rgb(var(--color-gray-700));
    margin: 0;
  }

  &__subtitle {
    font-size: 12px;
    color: rgb(var(--color-gray-500));
    margin: 2px 0 0 0;
    line-height: 1.4;
  }

  &__icon {
    color: rgb(var(--color-indigo-500));
  }

  &__body {
    background: hsl(var(--card));
  }
}

.ai-doc-card {
  @apply border border-solid border-border bg-card;

  &--bad {
    @apply border-red-200;
  }

  &--warn {
    @apply border-yellow-200;
  }

  &__icon {
    width: 28px;
    height: 28px;
    @apply rounded border border-solid;

    &--ok {
      @apply bg-green-50 border-green-100 text-green-500;
    }

    &--warn {
      @apply bg-yellow-50 border-yellow-100 text-yellow-500;
    }

    &--bad {
      @apply bg-red-50 border-red-100 text-red-400;
    }
  }

  &__name {
    @apply font-semibold mt-0.5;
    font-size: 12px;
    letter-spacing: -0.005em;
    color: rgb(var(--color-gray-700));
  }

  &__meta {
    font-size: 11px;
    color: rgb(var(--color-gray-500));
    line-height: 1.4;
    margin-top: 2px;
  }

  &__meta-status {
    font-size: 11px;
    line-height: 1.4;
    font-weight: 500;

    &--warn {
      color: rgb(var(--color-yellow-600));
    }

    &--bad {
      color: rgb(var(--color-red-500));
    }
  }

  &__chevron {
    @apply w-3.5 h-3.5;
    color: rgb(var(--color-gray-400));
    transition: color 0.12s ease;
  }

  &__panel {
    @apply border-0 border-t border-solid border-border;
  }

  &__panel-left {
    @apply md:border-0 md:border-r md:border-solid md:border-border bg-muted/40;
  }

  &__panel-right {
    background-color: hsl(var(--card));
  }

  &__section-title {
    @apply font-medium uppercase;
    font-size: 10px;
    letter-spacing: 0.04em;

    &--ai {
      color: rgb(var(--color-indigo-500));
    }

    &--rec {
      color: rgb(var(--color-green-500));
    }

    &--muted {
      color: rgb(var(--color-gray-400));
    }
  }

  &__group-title {
    font-size: 11px;
    font-weight: 600;
    color: rgb(var(--color-gray-600));
    letter-spacing: -0.005em;
  }

  &__section-text {
    font-size: 12.5px;
    line-height: 1.55;
    color: rgb(var(--color-gray-600));
  }

  &__field-key {
    font-size: 12px;
    color: rgb(var(--color-gray-400));
    line-height: 1.5;
  }

  &__field-val {
    font-size: 12px;
    line-height: 1.5;
  }

  &__empty {
    background-color: hsl(var(--muted) / 0.3);
    color: hsl(var(--muted-foreground));
  }

  &__empty-text {
    font-size: 12px;
    line-height: 1.5;
    color: hsl(var(--foreground));
    margin: 0;
    font-weight: 500;
  }

  &__empty-hint {
    font-size: 11px;
    line-height: 1.4;
    color: hsl(var(--muted-foreground));
    margin: 0;
    max-width: 320px;
  }
}

.ai-field--ok {
  color: rgb(var(--color-gray-700));
}

.ai-field--warn {
  color: rgb(var(--color-yellow-600));
}

.ai-field--bad {
  color: rgb(var(--color-red-500));
}

.reveal {
  //opacity: 0;
  //transform: translateY(6px);
  //animation: ai-check-doc-reveal 520ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
  //animation-delay: var(--reveal-delay, 0ms);
}

.extracted-row {
  border-bottom: 1px dashed rgb(var(--color-gray-200));
}

.extracted-row:last-child {
  border-bottom: 0;
}

/* Skeleton-карточки во время AI-проверки */
.ai-doc-card--skeleton {
  pointer-events: none;
}
.ai-doc-card__icon--skeleton {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgb(var(--color-gray-100)) 0%,
    rgb(var(--color-gray-200)) 50%,
    rgb(var(--color-gray-100)) 100%
  );
  animation: ai-doc-skeleton-shimmer 1.4s ease-in-out infinite;
}
.ai-doc-skeleton-bar {
  height: 10px;
  border-radius: 3px;
  background: linear-gradient(
    90deg,
    rgb(var(--color-gray-100)) 0%,
    rgb(var(--color-gray-200)) 50%,
    rgb(var(--color-gray-100)) 100%
  );
  background-size: 200% 100%;
  animation: ai-doc-skeleton-shimmer 1.4s ease-in-out infinite;
}
.ai-doc-skeleton-bar--title {
  width: 60%;
}
.ai-doc-skeleton-bar--meta {
  width: 35%;
  height: 8px;
}
@keyframes ai-doc-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

//@keyframes ai-check-doc-reveal {
//  to {
//    opacity: 1;
//    transform: translateY(0);
//  }
//}
</style>
